import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useFamily } from '@/contexts/FamilyContext'
import { apiClient } from '@/utils/api'
import { getTodayDateString, extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import { Flame, Star, ChevronLeft, ChevronRight, Calendar, X, Eye, TrendingUp } from 'lucide-react'
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, addYears, subYears, startOfYear } from 'date-fns'
import {
  usePointsHistory,
  useLeaderboard,
  usePointsBreakdown,
  LeaderboardPeriod,
} from '@/hooks/usePoints'

export const KidsPage: React.FC = () => {
  const { user } = useAuth()
  const { getChildren } = useFamily()
  const children = getChildren()
  const isParent = user?.is_parent

  const [activeTab, setActiveTab] = useState<'overview' | 'points' | 'leaderboard' | 'history'>('overview')
  const [selectedKidId, setSelectedKidId] = useState<string | null>(null)
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<LeaderboardPeriod>('month')
  const [selectedWeek, setSelectedWeek] = useState(new Date())
  const [selectedMonth, setSelectedMonth] = useState(new Date())
  const [selectedYear, setSelectedYear] = useState(new Date())
  const [showBreakdownModal, setShowBreakdownModal] = useState(false)
  const [selectedChildUsername, setSelectedChildUsername] = useState<string | null>(null)
  const [completedToday, setCompletedToday] = useState(0)
  const [weeklyCompletionRate, setWeeklyCompletionRate] = useState(0)
  const [overviewLoading, setOverviewLoading] = useState(true)

  // Default to first kid
  useEffect(() => {
    if (children.length > 0 && selectedKidId === null) {
      setSelectedKidId(children[0].id)
    }
  }, [children, selectedKidId])

  const selectedKid = children.find(c => c.id === selectedKidId) || children[0]

  // Calculate leaderboard offset
  const leaderboardOffset = useMemo(() => {
    const now = new Date()
    if (leaderboardPeriod === 'week') {
      const currentWeekStart = startOfWeek(now, { weekStartsOn: 0 })
      const selectedWeekStart = startOfWeek(selectedWeek, { weekStartsOn: 0 })
      return -Math.floor((currentWeekStart.getTime() - selectedWeekStart.getTime()) / (1000 * 60 * 60 * 24 * 7))
    } else if (leaderboardPeriod === 'month') {
      const currentMonthStart = startOfMonth(now)
      const selectedMonthStart = startOfMonth(selectedMonth)
      const yearDiff = currentMonthStart.getFullYear() - selectedMonthStart.getFullYear()
      const monthDiff = currentMonthStart.getMonth() - selectedMonthStart.getMonth()
      return -(yearDiff * 12 + monthDiff)
    } else if (leaderboardPeriod === 'year') {
      return -(startOfYear(now).getFullYear() - startOfYear(selectedYear).getFullYear())
    }
    return 0
  }, [leaderboardPeriod, selectedWeek, selectedMonth, selectedYear])

  // Data hooks
  const { data: historyData } = usePointsHistory(selectedKidId, 10)
  const { data: allHistoryData } = usePointsHistory(user?.id, 20)
  const { data: leaderboard = [], isLoading: leaderboardLoading } = useLeaderboard(leaderboardPeriod, leaderboardOffset)
  const { data: childBreakdown } = usePointsBreakdown(
    selectedChildUsername,
    leaderboardPeriod,
    Math.abs(leaderboardOffset)
  )

  // Load overview stats
  useEffect(() => {
    loadOverviewStats()
  }, [])

  const loadOverviewStats = async () => {
    setOverviewLoading(true)
    try {
      const todayStr = await getTodayDateString()
      const assignmentsRes = await apiClient.get('/api/assignments')
      const assignments = assignmentsRes.assignments || assignmentsRes.data || []
      setCompletedToday(assignments.filter((a: any) =>
        (a.status === 'completed' || a.status === 'verified') && a.completed_at && a.completed_at.startsWith(todayStr)
      ).length)

      const [year, month, day] = todayStr.split('-').map(Number)
      const weekAgoDate = new Date(year, month - 1, day - 7)
      const weekAgoStr = `${weekAgoDate.getFullYear()}-${String(weekAgoDate.getMonth() + 1).padStart(2, '0')}-${String(weekAgoDate.getDate()).padStart(2, '0')}`
      const weeklyAssignments = assignments.filter((a: any) => {
        const dueDateStr = a.due_date ? extractDateInTimezoneSync(a.due_date) : null
        return dueDateStr && dueDateStr >= weekAgoStr && dueDateStr <= todayStr
      })
      const weeklyCompleted = weeklyAssignments.filter((a: any) => a.status === 'completed' || a.status === 'verified').length
      setWeeklyCompletionRate(weeklyAssignments.length > 0 ? Math.round((weeklyCompleted / weeklyAssignments.length) * 100) : 0)
    } catch (error) {
      console.error('Failed to load overview stats:', error)
    }
    setOverviewLoading(false)
  }

  // Process history
  const pointsHistory = useMemo(() => {
    if (activeTab === 'history') {
      const data = selectedKidId ? historyData : allHistoryData
      const transactions = data?.transactions || []
      return transactions.map((t: any) => {
        const kid = children.find(c => c.id === (t.user_id || selectedKidId))
        return { ...t, user_name: kid?.display_name, user_color: kid?.color_theme }
      })
    }
    return []
  }, [historyData, allHistoryData, activeTab, selectedKidId, children])

  const totalFamilyPoints = leaderboard.reduce((sum, entry) => sum + (entry.points || 0), 0)
  const totalAvailablePoints = leaderboard.reduce((sum, entry) => sum + ((entry as any).available_points ?? entry.total_points ?? 0), 0)

  const fetchChildBreakdown = (username: string) => {
    setSelectedChildUsername(username)
    setShowBreakdownModal(true)
  }

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'chore_completion': return '✅'
      case 'weekly_bonus': return '🎯'
      case 'streak_bonus': return '🔥'
      case 'purchase': return '🛒'
      case 'trade': return '🔄'
      default: return '💰'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    if (diffHours < 1) return 'Just now'
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  const getLevelProgress = () => {
    if (!selectedKid) return 0
    const level = selectedKid.level ?? 1
    const xp = selectedKid.xp ?? 0
    const currentLevelXP = (level - 1) * 100
    const nextLevelXP = level * 100
    return Math.max(0, Math.min(100, ((xp - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100))
  }

  const getPeriodLabel = () => {
    switch (leaderboardPeriod) {
      case 'day': return 'Today'
      case 'week': return 'This Week'
      case 'month': return 'This Month'
      case 'year': return 'This Year'
      case 'all': return 'All Time'
    }
  }

  return (
    <div className="space-y-6">
      {/* Kids Cards - Always visible */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {children.map((kid) => (
          <button
            key={kid.id}
            onClick={() => {
              setSelectedKidId(kid.id)
              if (activeTab === 'overview') setActiveTab('points')
            }}
            className={`flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
              selectedKidId === kid.id
                ? 'bg-white shadow-md ring-2 ring-offset-1'
                : 'bg-white/60 shadow-sm hover:shadow-md hover:bg-white'
            }`}
            style={selectedKidId === kid.id ? { ringColor: kid.color_theme || '#6366f1' } : {}}
          >
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              style={{ backgroundColor: kid.color_theme || '#6366f1' }}
            >
              {kid.display_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900 text-sm truncate">{kid.display_name}</div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>Lv {kid.level ?? 1}</span>
                {(kid.streak_days ?? 0) > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Flame className="w-3 h-3 text-orange-500" />
                    {kid.streak_days}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="font-bold text-sm" style={{ color: kid.color_theme || '#6366f1' }}>
                {(kid.available_points ?? kid.total_points ?? 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-400 flex items-center gap-0.5 justify-end">
                <Star className="w-3 h-3" /> available
              </div>
              <div className="text-xs text-gray-300">
                {(kid.lifetime_points_earned ?? 0).toLocaleString()} lifetime
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg sticky top-0 z-20">
        {(['overview', 'points', 'leaderboard', 'history'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab === 'overview' ? 'Overview' : tab === 'points' ? 'Points' : tab === 'leaderboard' ? 'Leaderboard' : 'History'}
          </button>
        ))}
      </div>

      {/* Overview Tab - Family summary */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Available Points</div>
              <div className="text-3xl font-bold">{totalAvailablePoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">family total</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">{getPeriodLabel()}</div>
              <div className="text-3xl font-bold">{totalFamilyPoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">earned</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Weekly Rate</div>
              <div className="text-3xl font-bold">{weeklyCompletionRate}%</div>
              <div className="text-xs opacity-75 mt-1">completion rate</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Completed</div>
              <div className="text-3xl font-bold">{completedToday}</div>
              <div className="text-xs opacity-75 mt-1">chores today</div>
            </div>
          </div>

        </div>
      )}

      {/* Points Tab - Individual kid detail */}
      {activeTab === 'points' && selectedKid && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Available Points</div>
              <div className="text-3xl font-bold">{(selectedKid.available_points ?? selectedKid.total_points ?? 0).toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">available to spend</div>
            </div>
            <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Lifetime Earned</div>
              <div className="text-3xl font-bold">{(selectedKid.lifetime_points_earned ?? 0).toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">all-time earnings</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">This Week</div>
              <div className="text-3xl font-bold">{(selectedKid.weekly_points ?? 0).toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">earned this week</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Level</div>
              <div className="text-3xl font-bold">{selectedKid.level ?? 1}</div>
              <div className="text-xs opacity-75 mt-1">{selectedKid.xp ?? 0} XP total</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Streak</div>
              <div className="text-3xl font-bold">{selectedKid.streak_days ?? 0}</div>
              <div className="text-xs opacity-75 mt-1">days in a row</div>
            </div>
          </div>

          {/* Level Progress */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900">Level {selectedKid.level ?? 1}</h3>
              <span className="text-sm font-medium text-gray-600">
                {selectedKid.xp ?? 0} / {(selectedKid.level ?? 1) * 100} XP
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="h-4 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 transition-all duration-500"
                style={{ width: `${getLevelProgress()}%` }}
              />
            </div>
            <div className="text-sm text-gray-500 mt-2 text-center">
              {Math.round(100 - getLevelProgress())}% to Level {(selectedKid.level ?? 1) + 1}
            </div>
          </div>

          {/* Recent History for this kid */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-4">Recent Activity</h3>
            {(historyData?.transactions || []).length === 0 ? (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {(historyData?.transactions || []).slice(0, 5).map((t: any) => (
                  <div key={t.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{getTransactionIcon(t.transaction_type)}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{t.description}</div>
                        <div className="text-xs text-gray-500">{formatDate(t.created_at)}</div>
                      </div>
                    </div>
                    <span className={`font-bold ${t.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {t.points > 0 ? '+' : ''}{t.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="space-y-4">
          {/* Period Selector */}
          <div className="flex flex-wrap gap-2">
            {(['day', 'week', 'month', 'year', 'all'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setLeaderboardPeriod(period)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  leaderboardPeriod === period
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {period === 'day' ? 'Today' : period === 'week' ? 'Week' : period === 'month' ? 'Month' : period === 'year' ? 'Year' : 'All Time'}
              </button>
            ))}
          </div>

          {/* Period Navigator */}
          {leaderboardPeriod === 'week' && (
            <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
              <button onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))} className="p-2 rounded hover:bg-gray-100"><ChevronLeft className="w-5 h-5" /></button>
              <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(startOfWeek(selectedWeek, { weekStartsOn: 0 }), 'MMM d')} - {format(endOfWeek(selectedWeek, { weekStartsOn: 0 }), 'MMM d, yyyy')}
              </div>
              <button onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))} className="p-2 rounded hover:bg-gray-100" disabled={selectedWeek >= new Date()}><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}
          {leaderboardPeriod === 'month' && (
            <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
              <button onClick={() => setSelectedMonth(subMonths(selectedMonth, 1))} className="p-2 rounded hover:bg-gray-100"><ChevronLeft className="w-5 h-5" /></button>
              <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(selectedMonth, 'MMMM yyyy')}
              </div>
              <button onClick={() => setSelectedMonth(addMonths(selectedMonth, 1))} className="p-2 rounded hover:bg-gray-100" disabled={startOfMonth(selectedMonth) >= startOfMonth(new Date())}><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}
          {leaderboardPeriod === 'year' && (
            <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm">
              <button onClick={() => setSelectedYear(subYears(selectedYear, 1))} className="p-2 rounded hover:bg-gray-100"><ChevronLeft className="w-5 h-5" /></button>
              <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(selectedYear, 'yyyy')}
              </div>
              <button onClick={() => setSelectedYear(addYears(selectedYear, 1))} className="p-2 rounded hover:bg-gray-100" disabled={startOfYear(selectedYear) >= startOfYear(new Date())}><ChevronRight className="w-5 h-5" /></button>
            </div>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Family Total</div>
              <div className="text-3xl font-bold">{totalFamilyPoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">{getPeriodLabel()?.toLowerCase()}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Available</div>
              <div className="text-3xl font-bold">{totalAvailablePoints.toLocaleString()}</div>
              <div className="text-xs opacity-75 mt-1">to spend</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white shadow-lg">
              <div className="text-sm font-medium opacity-90 mb-1">Kids</div>
              <div className="text-3xl font-bold">{leaderboard.length}</div>
              <div className="text-xs opacity-75 mt-1">family members</div>
            </div>
          </div>

          {/* Leaderboard Rows */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Family Leaderboard
              <span className="text-sm font-normal text-gray-500 ml-2">{getPeriodLabel()}</span>
            </h2>
            {leaderboard.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No data yet for this period.</p>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((entry) => {
                  const isTop3 = entry.rank <= 3
                  const medalColors: Record<number, string> = { 1: 'from-yellow-400 to-amber-500', 2: 'from-gray-300 to-gray-400', 3: 'from-orange-400 to-amber-600' }
                  const medalEmoji: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' }
                  return (
                    <div
                      key={entry.id}
                      onClick={() => fetchChildBreakdown(entry.username)}
                      className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all hover:shadow-lg ${
                        isTop3 ? `bg-gradient-to-r ${medalColors[entry.rank]} text-white shadow-md` : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        {isTop3 ? <div className="text-3xl">{medalEmoji[entry.rank]}</div> : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600">#{entry.rank}</div>
                        )}
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm ${isTop3 ? 'ring-2 ring-white/50' : ''}`}
                          style={{ backgroundColor: entry.color_theme || '#6366f1' }}
                        >
                          {entry.display_name[0]}
                        </div>
                        <div>
                          <div className={`font-semibold flex items-center gap-2 ${isTop3 ? 'text-white' : 'text-gray-900'}`}>
                            {entry.display_name}
                            <Eye className={`w-4 h-4 ${isTop3 ? 'text-white/70' : 'text-gray-400'}`} />
                          </div>
                          <div className={`text-sm ${isTop3 ? 'text-white/80' : 'text-gray-500'}`}>
                            Level {entry.level} • {entry.streak_days} day streak
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${isTop3 ? 'text-white' : ''}`} style={isTop3 ? {} : { color: entry.color_theme || '#6366f1' }}>
                          {entry.points?.toLocaleString()}
                        </div>
                        <div className={`text-sm ${isTop3 ? 'text-white/80' : 'text-gray-500'}`}>
                          {getPeriodLabel()?.toLowerCase()}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          <h2 className="text-xl font-semibold mb-2">Recent Points Activity</h2>
          <p className="text-sm text-gray-600 mb-4">
            {selectedKidId ? `Last 10 transactions for ${selectedKid?.display_name}` : 'Last 20 transactions across all kids'}
          </p>
          {pointsHistory.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 text-center py-8">
              <p className="text-gray-500">No points history yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="divide-y divide-gray-100">
                {pointsHistory.map((transaction: any) => (
                  <div key={transaction.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{getTransactionIcon(transaction.transaction_type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          {transaction.description}
                          {transaction.user_name && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: transaction.user_color || '#6B7280' }}
                            >
                              {transaction.user_name}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <span>{formatDate(transaction.created_at)}</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-400 text-xs">
                            {new Date(transaction.created_at).toLocaleDateString('en-US', {
                              weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className={`font-bold text-lg whitespace-nowrap ${transaction.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {transaction.points > 0 ? '+' : ''}{transaction.points}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Points Breakdown Modal */}
      {showBreakdownModal && childBreakdown && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{childBreakdown.user}</h2>
                  <p className="text-sm opacity-90">Points Statement</p>
                  <p className="text-xs opacity-75 mt-1">{childBreakdown.period}</p>
                </div>
                <button onClick={() => { setShowBreakdownModal(false); setSelectedChildUsername(null) }} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {childBreakdown.breakdown.chores && childBreakdown.breakdown.chores.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">CHORES COMPLETED</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.chores.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description.replace('Completed: ', '')}</span>
                        </div>
                        <span className="text-green-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-green-600 font-mono font-bold">+{childBreakdown.totals.earned}</span>
                  </div>
                </div>
              )}
              {childBreakdown.breakdown.bonuses && childBreakdown.breakdown.bonuses.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">BONUSES</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.bonuses.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-blue-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-blue-600 font-mono font-bold">+{childBreakdown.totals.bonuses}</span>
                  </div>
                </div>
              )}
              {childBreakdown.breakdown.deductions && childBreakdown.breakdown.deductions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">DEDUCTIONS</h3>
                  <div className="space-y-1">
                    {childBreakdown.breakdown.deductions.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-red-600 font-mono">{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-red-600 font-mono font-bold">{childBreakdown.totals.deductions}</span>
                  </div>
                </div>
              )}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs font-semibold text-gray-500 mb-4">CALCULATION</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600"><span>Earned from Chores</span><span className="font-mono">+{childBreakdown.totals.earned}</span></div>
                  <div className="flex justify-between text-gray-600"><span>Bonuses</span><span className="font-mono">+{childBreakdown.totals.bonuses}</span></div>
                  <div className="flex justify-between text-gray-600"><span>Deductions</span><span className="font-mono">{childBreakdown.totals.deductions}</span></div>
                  {childBreakdown.totals.adjustments !== 0 && (
                    <div className="flex justify-between text-gray-600"><span>Adjustments</span><span className="font-mono">{childBreakdown.totals.adjustments > 0 ? '+' : ''}{childBreakdown.totals.adjustments}</span></div>
                  )}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                    <span>PERIOD TOTAL</span>
                    <span className="font-mono text-blue-600 text-lg">{childBreakdown.totals.leaderboard_total}</span>
                  </div>
                </div>
                {childBreakdown.totals.spent > 0 && (
                  <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                    <div className="flex justify-between text-gray-600"><span>Rewards Redeemed</span><span className="font-mono">-{childBreakdown.totals.spent}</span></div>
                    <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                      <span>AVAILABLE BALANCE</span>
                      <span className="font-mono text-green-600 text-lg">{childBreakdown.totals.available_balance}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
